// A deadline covers both headers and JSON. Diagnostics never contain the URL,
// response body, account, filename or credentials.
import { retryRead, retryAfterMilliseconds } from './retryRead.mjs';

export async function readLatestLandingJob(url, { signal, headers, timeoutMs = 12000,
  fetchImpl = globalThis.fetch, onDiagnostic = () => {}, visibility = () => 'visible' } = {}) {
  return retryRead(remaining => readLatestLandingJobOnce(url, { signal, headers,
    timeoutMs: Math.min(timeoutMs, remaining), fetchImpl, visibility,
    onDiagnostic: (name, values) => {
      if (!['history_load_error','api_request_cancelled'].includes(name)) onDiagnostic(name, values);
    } }), { signal, onRecovered: values => onDiagnostic('api_read_recovered', { operation: 'history', ...values }) })
    .catch(error => {
      try { onDiagnostic(error.code === 'request_cancelled' ? 'api_request_cancelled' : 'history_load_error', {
        operation: 'history', error_code: error.code, termination_reason: error.termination_reason,
        ...Object.fromEntries(['event_source','elapsed_ms','header_ms','body_ms','auth_ms','db_ms','render_ms']
          .filter(key => error[key] !== undefined).map(key => [key, error[key]])),
        ...(error.status ? { status_code: error.status } : {}),
      }); } catch {}
      throw error;
    });
}

async function readLatestLandingJobOnce(url, { signal, headers, timeoutMs,
  fetchImpl, onDiagnostic, visibility }) {
  const controller = new AbortController(), started = Date.now(), startedVisibility = visibility();
  let headersAt, response, timedOut = false, stop, serverTimes = {};
  const measures = () => ({ operation: 'history',
    event_source: `history_landing_latest_${startedVisibility === visibility() ? startedVisibility
      : visibility() === 'visible' ? 'became_visible' : 'became_hidden'}`,
    elapsed_ms: Math.max(0, Date.now() - started), ...serverTimes,
    ...(headersAt === undefined ? {} : { header_ms: Math.max(0, headersAt - started), body_ms: Math.max(0, Date.now() - headersAt) }),
  });
  const emit = (name,values) => { try { onDiagnostic(name,values); } catch {} };
  const abort = () => controller.abort(signal?.reason || 'cancelled');
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) abort();
  const timer = setTimeout(() => { timedOut = true; controller.abort('timeout'); }, timeoutMs);
  const cancelled = () => Object.assign(new Error(timedOut ? 'History request timed out.' : 'History request cancelled.'), {
    name: timedOut ? 'Error' : 'AbortError', code: timedOut ? 'request_timeout' : 'request_cancelled',
    termination_reason: timedOut ? 'timeout' : ['account_changed','replaced','navigation'].includes(controller.signal.reason)
      ? controller.signal.reason : 'cancelled',
  });
  const interrupted = new Promise((_, reject) => {
    stop = () => reject(cancelled());
    controller.signal.addEventListener('abort', stop, { once: true });
    if (controller.signal.aborted) stop();
  });
  try {
    return await Promise.race([interrupted, (async () => {
      if (controller.signal.aborted) throw cancelled();
      response = await fetchImpl(url, { headers, signal: controller.signal, cache: 'no-store' });
      if (controller.signal.aborted) throw cancelled();
      headersAt = Date.now();
      for (const part of (response.headers?.get?.('Server-Timing') || '').split(',')) {
        const match = part.trim().match(/^(auth|db|render);\s*dur=([\d.]+)$/);
        if (match && Number(match[2]) <= 86400000) serverTimes[match[1] + '_ms'] = Number(match[2]);
      }
      let jobs;
      try { jobs = await response.json(); }
      catch {
        if (controller.signal.aborted) throw cancelled();
        if (response.ok) throw Object.assign(new Error('History response is invalid.'), { code: 'malformed_response', termination_reason: 'parse' });
      }
      if (controller.signal.aborted || Date.now() - started >= timeoutMs) {
        if (!controller.signal.aborted) { timedOut = true; controller.abort('timeout'); }
        throw cancelled();
      }
      if (!response.ok) throw Object.assign(new Error('History is temporarily unavailable.'), {
        status: response.status, code: jobs?.code === 'auth_verification_unavailable' ? jobs.code : `http_${response.status}`,
        retryAfterMs: retryAfterMilliseconds(response.headers),
        termination_reason: 'http_error',
      });
      if (!Array.isArray(jobs)) throw Object.assign(new Error('History response is invalid.'), { code: 'malformed_response', termination_reason: 'parse' });
      const timing = { ...measures(), termination_reason: 'response' };
      if (timing.elapsed_ms >= 2000) emit('api_read_slow', timing);
      return jobs[0] || null;
    })()]);
  } catch (error) {
    const failure = controller.signal.aborted ? cancelled() : Object.assign(new Error('History could not be refreshed.'), {
      code: ['malformed_response','auth_verification_unavailable'].includes(error.code) || /^http_[45][0-9]{2}$/.test(error.code || '')
        ? error.code : 'request_network_error',
      termination_reason: ['http_error','parse'].includes(error.termination_reason) ? error.termination_reason : 'network',
      ...(error.status ? { status: error.status } : {}),
      ...(error.retryAfterMs ? { retryAfterMs: error.retryAfterMs } : {}),
    });
    Object.assign(failure, measures());
    emit('history_load_error', { ...measures(), error_code: failure.code,
      termination_reason: failure.termination_reason, ...(failure.status ? { status_code: failure.status } : {}) });
    throw failure;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
    controller.signal.removeEventListener('abort', stop);
  }
}
